import { Box, HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { Button, Infobox } from 'Atoms';
import {
  GetCategoryAndStandardsQuery_,
  GetOnboardingEsrsAssessmentDocument_,
  GetParentMaterialityAssessmentQuery_,
  GetSelectedDataPointsQuery_,
  useGetAssessmentEsrs2MaterialMetricsQuery,
  useGetCategoryAndStandardsQuery,
  useGetOnboardingEsrsAssessmentQuery,
  useGetParentMaterialityAssessmentQuery,
  useGetSelectedDataPointsQuery,
  useUpdateAssessmentOnboardingMutation,
} from 'models';
import { ContentLayout, Loader } from 'Molecules';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography } from 'Tokens';
import { EsrsAssessmentConfig, FrameworkRefs } from '../Assessment';
import { MaterialityAssessment } from '../MaterialityAssessment';
import { ActiveStepLabels } from 'containers/Esrs/EsrsUtilComponents';
import { DataCollectionSetup } from '../DataCollection/DataCollectionSetup';
import { useCurrentCompany, useCurrentCompanyId } from 'utils/hooks';
import { DoubleMaterialityAssessment } from '../MaterialityAssessment/DoubleMaterialityAssessment/DoubleMaterialityAssessment';
import { LearnAboutSustainability } from './LearnAboutSustainability';
import { ONBOARDING_INFORMATION } from './OnboardingInformation';
import { DatapointBrowserBox } from '../Assessment/DatapointBrowser/DatapointBrowserBox/DatapointBrowserBox';
import { DataPointsBank } from '../MaterialityAssessment/DataPointsBank/DataPointsBank';
import { useFeatureFlags } from 'containers/Navigation';
import { useKnowledgeBase } from 'Features';

export const GUIDE_DOCUMENT_ID = '1NwuUz3RxG26BmcN3CPF1-26F7VH01bbFbgDWxPa5a6E';
export const SHEET_NUMBER = '606737876';

const AssessmentPath = ({ framework, step }: { framework: FrameworkRefs; step: number }) => {
  const [updateOnboardingStep] = useUpdateAssessmentOnboardingMutation();
  const { esrsAssessmentId } = useParams();

  const goToStep = (curStep: number) => {
    if (curStep <= 1) return;
    updateOnboardingStep({
      variables: {
        id: esrsAssessmentId,
        onboardingStep: curStep,
      },
    });
  };

  return (
    <VStack width="20%" p="20px 0px" h="100%" alignItems="flex-start">
      <Box position="relative">
        <Box borderLeft="1px dashed" pl="32px" borderColor="border.hover" pb="24px" ml="14px">
          <ActiveStepLabels
            left="0"
            bgColor="bg.muted"
            title={`Learn about ${ONBOARDING_INFORMATION[framework]?.title}`}
            active={step === 1}
            done={step > 1}
            goBack={() => goToStep(1)}
          />
        </Box>
        <Box borderLeft="1px dashed" pl="32px" borderColor="border.hover" pb="24px" ml="14px">
          <ActiveStepLabels
            left="0"
            title="Set company structure"
            active={step === 2}
            bgColor="bg.muted"
            done={step > 2}
            goBack={() => goToStep(2)}
          />
        </Box>
        <Box borderLeft="1px dashed" pl="32px" borderColor="border.hover" pb="24px" ml="14px">
          <ActiveStepLabels
            left="0"
            title={ONBOARDING_INFORMATION[framework]?.materialityAssessment.title}
            active={step === 3}
            bgColor="bg.muted"
            done={step > 3}
            goBack={() => goToStep(3)}
          />
        </Box>
        <Box pl="32px" pb="24px" ml="14px">
          <ActiveStepLabels
            left="0"
            bgColor="bg.muted"
            title={ONBOARDING_INFORMATION[framework]?.dataCollectionSetup.title}
            active={step === 4}
            done={step > 4}
            goBack={() => goToStep(4)}
          />
        </Box>
      </Box>
    </VStack>
  );
};

const MaterialityAssessmentStep = ({
  framework,
  setStep,
  isOnboarding = false,
  isCollectOnly = false,
  allStandardsAndCategories,
  selectedDataPointsFromOtherFrameWork,
  parentAssessmentData,
  hasOtherMetrics,
}: {
  framework: FrameworkRefs;
  setStep: (step: number) => void;
  isOnboarding?: boolean;
  isCollectOnly?: boolean;
  allStandardsAndCategories?: GetCategoryAndStandardsQuery_['MFCategory'];
  selectedDataPointsFromOtherFrameWork?: GetSelectedDataPointsQuery_['EsrsMetric'];
  parentAssessmentData?: GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk'];
  hasOtherMetrics: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { onOpen: onKnowledgeBaseOpen } = useKnowledgeBase();

  const information = ONBOARDING_INFORMATION[framework].materialityAssessment;
  return (
    <VStack w="100%" alignItems="start" spacing={isCollectOnly ? '16px' : '32px'}>
      <VStack alignItems="start" gap="4px">
        <Typography variant="h1">{information.title}</Typography>
        {information.description && (
          <Typography variant="body">{information.description}</Typography>
        )}
      </VStack>
      {!isCollectOnly &&
        information.info?.steps.map((step) => (
          <VStack alignItems="start">
            <VStack gap="2px" alignItems="start">
              {step.title && <Typography variant="h2">{step.title}</Typography>}
              <Typography
                variant="body"
                color="text.muted"
                fontSize="14px"
                fontWeight="400"
                lineHeight="20px"
              >
                {step.description}
              </Typography>
            </VStack>

            {step.infobox && <Infobox {...step.infobox} />}
          </VStack>
        ))}{' '}
      <VStack mt={isOnboarding ? '0px' : '28px'} w="100%" alignItems="stretch" spacing="24px">
        <VStack gap="2px" alignItems="start">
          <Typography variant="h2">{information.table.title}</Typography>
          <Typography
            variant="body"
            color="text.muted"
            fontSize="14px"
            fontWeight="400"
            lineHeight="20px"
          >
            {information.table.description}
          </Typography>
        </VStack>
        <MaterialityAssessment setStep={setStep} isOnboarding={isOnboarding} />
      </VStack>
      {hasOtherMetrics && (
        <>
          <VStack w="100%" gap={'16px'}>
            <VStack
              gap={'2px'}
              alignItems={'start'}
              borderBottom={'1px solid  rgba(15, 15, 46, 0.12)'}
              paddingBottom={'8px'}
              w="100%"
            >
              <Typography variant="h2">Other frameworks (optional)</Typography>
              <Typography
                variant="body"
                color="text.muted"
                fontSize="14px"
                fontWeight="400"
                lineHeight="20px"
              >
                Optional step. You can browse and add entity specific data points to add to your
                report
              </Typography>
            </VStack>
          </VStack>
          <DatapointBrowserBox
            onClickBrowse={() => onOpen()}
            onClickLearnMore={() => onKnowledgeBaseOpen('mf-data-points-database')}
          />
          <DataPointsBank
            onClose={onClose}
            isOpen={isOpen}
            allStandardsAndCategoriesData={allStandardsAndCategories}
            selectedDataPointsFromOtherFrameWork={selectedDataPointsFromOtherFrameWork}
            parentAssessmentData={parentAssessmentData}
            isCollectOnly={isCollectOnly}
          />
        </>
      )}
    </VStack>
  );
};

export const Onboarding = () => {
  const navigate = useNavigate();
  const { esrsAssessmentId = '', framework = '' } = useParams();
  const [step, setStep] = useState(1);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [lastSavedStep, setLastSavedStep] = useState(0);
  const { companyId } = useCurrentCompanyId();

  const [updateOnboardingStep] = useUpdateAssessmentOnboardingMutation();

  const { data, loading } = useGetOnboardingEsrsAssessmentQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const { data: esrs2MaterialMetrics, loading: loadingEsrs2Metrics } =
    useGetAssessmentEsrs2MaterialMetricsQuery({
      variables: { esrsAssessmentId: esrsAssessmentId },
      skip: !esrsAssessmentId,
    });
  const { company } = useCurrentCompany();

  const { companyFrameworks } = useFeatureFlags({ company });

  const hasOtherMetrics = useMemo(() => companyFrameworks.length >= 2, [companyFrameworks]);

  const { data: allStandardsAndCategories, loading: loadingAllStandardsAndCategories } =
    useGetCategoryAndStandardsQuery({
      variables: {
        assessmentId: esrsAssessmentId,
        framework,
        frameworkRefs: companyFrameworks,
      },
      skip: !esrsAssessmentId,
    });

  const {
    data: selectedDataPointsFromOtherFrameWork,
    loading: selectedDataPointsFromOtherFrameWorkLoading,
  } = useGetSelectedDataPointsQuery({
    variables: {
      assessmentId: esrsAssessmentId,
      framework,
    },
    skip: !esrsAssessmentId || !framework,
  });

  const { data: parentData, loading: parentDataLoading } = useGetParentMaterialityAssessmentQuery({
    variables: {
      childAssessmentId: esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const esrsAssessment = useMemo(() => data?.esrsAssessment, [data]);

  const assessmentStep = useMemo(() => esrsAssessment?.onboardingStep, [esrsAssessment]);

  const allStandardsAndCategoriesData = useMemo(
    () => allStandardsAndCategories?.MFCategory,
    [allStandardsAndCategories]
  );

  const selectedDataPointsFromOtherFrameWorkData = useMemo(
    () => selectedDataPointsFromOtherFrameWork?.EsrsMetric,
    [selectedDataPointsFromOtherFrameWork]
  );

  const parentAssessmentData = useMemo(() => parentData?.EsrsAssessment_by_pk, [parentData]);

  useEffect(() => {
    if (assessmentStep) {
      setStep(assessmentStep);
      setLastSavedStep(assessmentStep);
    }
  }, [assessmentStep]);

  if (
    loading ||
    loadingEsrs2Metrics ||
    loadingAllStandardsAndCategories ||
    selectedDataPointsFromOtherFrameWorkLoading ||
    parentDataLoading
  ) {
    return <Loader />;
  }

  const handleNextStep = () => {
    if (nextDisabled) {
      setShowAlertMessage(true);
    } else {
      if (step + 1 > lastSavedStep) {
        updateOnboardingStep({
          variables: {
            id: esrsAssessmentId,
            onboardingStep: step + 1,
          },
          refetchQueries: [GetOnboardingEsrsAssessmentDocument_],
        }).then((res) =>
          setLastSavedStep(res.data?.update_EsrsAssessment_by_pk?.onboardingStep ?? 0)
        );
      }
      setShowAlertMessage(false);
    }
  };

  const handlePrevStep = () => {
    if (step <= 1) return;
    updateOnboardingStep({
      variables: {
        id: esrsAssessmentId,
        onboardingStep: step - 1,
      },
      refetchQueries: [GetOnboardingEsrsAssessmentDocument_],
    }).then((res) => setLastSavedStep(res.data?.update_EsrsAssessment_by_pk?.onboardingStep ?? 0));

    setStep(step - 1);
    setNextDisabled(false);
    setShowAlertMessage(false);
  };

  return (
    <ContentLayout backButton={true} header={false} paddingBottom="0px !important">
      <HStack alignSelf="center" flexGrow="1" w="100%" alignItems="stretch">
        {step < 5 ? (
          <AssessmentPath framework={framework as FrameworkRefs} step={step} />
        ) : (
          <Box w="20%" />
        )}
        <VStack
          width="60%"
          bg="bg.default"
          border="1px solid"
          borderColor="border.decorative"
          borderRadius="8px"
          alignItems="start"
          justifyContent="space-between"
          p="24px 24px 0"
          position="relative"
          boxShadow="0px 0px 12px 0px rgba(15, 15, 46, 0.04), inset 0px 0px 0px 0px rgba(0, 0, 0, 0.08)"
        >
          {step === 1 && (
            <LearnAboutSustainability
              framework={framework as FrameworkRefs}
              esrsAssessment={esrsAssessment}
              setNextDisabled={setNextButtonDisabled}
              esrs2MaterialMetrics={esrs2MaterialMetrics}
            />
          )}
          {step === 2 && (
            <EsrsAssessmentConfig
              isOnboarding={true}
              nextDisabled={nextDisabled}
              setNextDisabled={setNextDisabled}
              showAlertMessage={showAlertMessage}
            />
          )}
          {step === 3 && (
            <MaterialityAssessmentStep
              framework={framework as FrameworkRefs}
              setStep={setStep}
              isOnboarding={true}
              isCollectOnly={esrsAssessment?.collectOnly ?? false}
              allStandardsAndCategories={allStandardsAndCategoriesData}
              selectedDataPointsFromOtherFrameWork={selectedDataPointsFromOtherFrameWorkData}
              parentAssessmentData={parentAssessmentData}
              hasOtherMetrics={hasOtherMetrics}
            />
          )}
          {step === 4 && <DataCollectionSetup setStep={setStep} isOnboarding={true} />}
          {step === 5 && <DoubleMaterialityAssessment onboarding={true} />}
          {step < 5 && (
            <VStack
              position="sticky"
              bottom="0"
              left="0"
              right="0"
              w="calc(100% + 48px)"
              backgroundColor="inherit"
              borderTop="1px solid"
              borderColor="border.decorative"
              borderBottomRadius="8px"
              ml="-24px"
            >
              <HStack justifyContent="flex-end" p="16px" alignSelf="flex-end">
                {step > 1 && (
                  <Button variant="ghost" onClick={() => handlePrevStep()}>
                    Prev. step
                  </Button>
                )}
                {step < 4 && (
                  <Button
                    variant="primary"
                    onClick={handleNextStep}
                    isDisabled={nextButtonDisabled}
                  >
                    Next step
                  </Button>
                )}
                {step === 4 && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      updateOnboardingStep({
                        variables: {
                          id: esrsAssessmentId,
                          onboardingStep: 0,
                        },
                        refetchQueries: [GetOnboardingEsrsAssessmentDocument_],
                      }).then(() => {
                        if (history.length > 1) {
                          navigate(
                            `/${companyId}/sustainability-reporting/${framework.toLocaleLowerCase()}/${esrsAssessmentId}`
                          );
                        } else {
                          const currentUrl = window.location.href;
                          window.location.href = currentUrl.replace(/\/[^/]+\/?$/, '');
                        }
                      });
                    }}
                  >
                    Done
                  </Button>
                )}
              </HStack>
            </VStack>
          )}
        </VStack>
      </HStack>
    </ContentLayout>
  );
};
